import { Link } from 'react-router-dom'
import { ShieldCheck, Server, Tag, BadgeCheck, FileUp, Mail, PenLine, FileCheck } from 'lucide-react'
import SeoHead, { ORGANIZATION_LD } from '../components/SeoHead'

const JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'W.Sign',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Windows, Web',
    url: 'https://sign.wsoft.tr/',
    description:
      'Kurumsal e-imza ve doğrulama altyapısı: CAdES, XAdES ve PAdES imza üretimi, uzun dönemli imza, RFC 3161 zaman damgası, OCSP/CRL doğrulama ve PKCS#11 token desteği.',
    inLanguage: 'tr-TR',
    publisher: ORGANIZATION_LD,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'W.Sign hangi imza formatlarını üretir?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'CAdES (BES, T ve ESXLong uzun dönemli), XAdES (B-B, T ve X-L) ve PAdES (B-B, B-T ve B-LT — PDF içine gömülü, DSS/VRI ile uzun dönemli). Üretilen imzalar gerçek nitelikli sertifikayla İmzager üzerinde doğrulanmıştır.',
        },
      },
      {
        '@type': 'Question',
        name: 'Belgelerim sizin sunucunuza yükleniyor mu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Portal akışında belge yalnızca imza çerçevesinin kurulması için sunucuda işlenir ve imza atma anında bilgisayarınıza inmez. Kendi altyapınızda çalıştırmak isterseniz W.Sign kurum içinde de kurulabilir; her iki durumda da kullanım telemetrisi toplanmaz.',
        },
      },
      {
        '@type': 'Question',
        name: 'Kendi yazılımıma e-imza ekleyebilir miyim?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Evet. W.Sign kendi uygulamanızdan çağırabileceğiniz bir imzalama servisi olarak da kullanılabilir; kullanıcı imza için yönlendirilir, imza tamamlandığında sonucu uygulamanıza döner. Entegrasyon için bizimle iletişime geçin.',
        },
      },
      {
        '@type': 'Question',
        name: 'Hangi e-imza kartları destekleniyor?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'PKCS#11 destekli nitelikli elektronik imza kartları ve USB tokenlar — AKİS, SafeNet eToken ve Gemalto dahil. 32-bit sürücüler için ayrı bir adım gerekmez.',
        },
      },
    ],
  },
]

export default function HomePage() {
  return (
    <>
      <SeoHead
        title="W.Sign — Kurumsal e-imza ve doğrulama"
        description="Belge yükleyin, e-posta veya bağlantıyla uzaktan imzalatın. CAdES, XAdES ve PAdES imza üretimi ve doğrulama; uzun dönemli imza, KamuSM zaman damgası, AKİS/SafeNet token desteği. Telemetri toplanmaz."
        path="/"
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="container-page pt-16 pb-12 md:pt-24 md:pb-20">
        <div className="max-w-3xl">
          <span className="badge">Kurumsal e-imza</span>
          <h1 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-[var(--color-fg)]">
            İmzalayın. <span className="text-[var(--color-accent)]">Doğrulayın.</span> Güvende kalın.
          </h1>
          <p className="mt-5 text-lg text-[var(--color-fg-muted)] max-w-2xl">
            CAdES, XAdES, PAdES imza ve doğrulama; AKİS, SafeNet ve Gemalto token desteği.
            KamuSM ile RFC 3161 zaman damgası, OCSP ve CRL doğrulama; sertifikanızın süresi
            dolduktan sonra da doğrulanabilen uzun dönemli imza. Belgeleriniz sizin
            kontrolünüzde kalır, kullanım telemetrisi toplanmaz.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/hash-sign" className="btn-primary">Hemen imzala</Link>
            <Link to="/verify" className="btn-ghost">İmza doğrula</Link>
            <Link to="/download" className="btn-ghost">Uygulamayı indir</Link>
          </div>
        </div>
      </section>

      {/* Why W.Sign — trust pillars */}
      <section className="container-page py-12">
        <h2 className="text-2xl font-semibold">Neden W.Sign?</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <PillarCard
            icon={ShieldCheck}
            title="Tam format desteği"
            body="CAdES, XAdES ve PAdES imza üretimi ile doğrulama; PDF içine gömülü imza ve uzun dönemli imza dahil tek altyapıda."
          />
          <PillarCard
            icon={Tag}
            title="Esnek lisanslama"
            body="Kurumunuzun ihtiyacına göre ölçeklenen lisans modeli. Size özel teklif için bizimle iletişime geçin."
          />
          <PillarCard
            icon={Server}
            title="Veri egemenliği"
            body="W.Sign kendi altyapınızda çalışabilir; belgeleriniz bizim sunucumuza hiç gelmez. Telemetri yok."
          />
          <PillarCard
            icon={BadgeCheck}
            title="Çalışan ürün"
            body="Gerçek nitelikli e-imza kartlarıyla doğrulanmış, canlı imza ve doğrulama portalı."
          />
        </div>
      </section>

      {/* Remote signing flow */}
      <section className="container-page py-12">
        <h2 className="text-2xl font-semibold">Belgenizi gönderin, uzaktan imzalatın</h2>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-2xl">
          Bir belgeyi yükleyin, imzalayacak kişiye e-posta veya bağlantı olarak gönderin;
          karşı taraf belgeyi inceleyip kendi nitelikli e-imzasıyla imzalasın. Her adımı
          panelinizden takip edin.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FlowStep
            n={1}
            icon={FileUp}
            title="Belgeyi hazırlayın"
            body="Hazır bir PDF / dosya yükleyin ya da metin yazıp PDF'e dönüştürün."
          />
          <FlowStep
            n={2}
            icon={Mail}
            title="İmzalayanı davet edin"
            body="Alıcının e-postasını girin; W.Sign ona kişisel bir imza bağlantısı gönderir. Bağlantıyı dilerseniz elinizle de paylaşabilirsiniz."
          />
          <FlowStep
            n={3}
            icon={PenLine}
            title="Karşı taraf imzalar"
            body="Alıcı bağlantıdan belgeyi görüntüler ve kendi nitelikli e-imzasıyla imzalar — hesap açmasına gerek yoktur."
          />
          <FlowStep
            n={4}
            icon={FileCheck}
            title="İmzalı belgeyi alın"
            body="İmza tamamlanınca durumu panelden görürsünüz. PDF gönderdiyseniz imzası içine gömülü PDF, diğer dosyalarda imza dosyası (.p7s) iner; ikisini de doğrulayabilirsiniz."
          />
        </div>

        <div className="mt-6">
          <Link to="/login" className="btn-primary">İmzalatmaya başla</Link>
        </div>
      </section>

      {/* Format matrix */}
      <section className="container-page py-12">
        <h2 className="text-2xl font-semibold">Neler destekleniyor?</h2>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-2xl">
          Nitelikli e-imza akışlarının kullandığı tüm format ve protokoller —
          ETSI / ITU spesifikasyonlarına uygun.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FormatCard
            title="CAdES"
            sub="ETSI EN 319 122"
            items={['BES — temel imza', 'T — RFC 3161 zaman damgası', 'ESXLong — uzun dönemli imza', 'Co-Sign — paralel imza']}
            status="ready"
          />
          <FormatCard
            title="XAdES"
            sub="ETSI EN 319 132"
            items={['B-B enveloped', 'Enveloping (binary embed)', 'Detached (URI ref)', 'X-L — uzun dönemli imza']}
            status="ready"
          />
          <FormatCard
            title="PAdES"
            sub="ETSI EN 319 142"
            items={['B-B — PDF içine gömülü imza', 'B-T — zaman damgalı', 'B-LT — DSS/VRI ile uzun dönemli', 'Acrobat imza panelinde görünür']}
            status="ready"
          />
          <FormatCard
            title="PKCS#11 Tokenlar"
            sub="32-bit + 64-bit sürücüler"
            items={['AKİS (TÜBİTAK)', 'SafeNet eToken', 'Gemalto', 'Hot-plug + reload']}
            status="ready"
          />
          <FormatCard
            title="Zaman Damgası"
            sub="RFC 3161"
            items={['KamuSM TSA özel auth', 'OID politika seçimi', 'SHA-256 / SHA-512 imprint', 'Self-test (in-memory TSA)']}
            status="ready"
          />
          <FormatCard
            title="Doğrulama"
            sub="OCSP + CRL"
            items={['AIA chain build', 'OCSP HTTP', 'CRL cache + delta', 'Embedded revocation (XL)']}
            status="ready"
          />
        </div>
      </section>

      {/* Usage */}
      <section className="container-page py-12">
        <h2 className="text-2xl font-semibold">Nasıl kullanılır?</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <UsageCard
            title="Web Portal"
            sub="Tarayıcıdan imza ve doğrulama"
            body="Belgenizi yükleyin, imzalayın veya bir imzayı doğrulayın — kurulum gerekmez."
            cta={{ label: 'İmzala', to: '/sign' }}
          />
          <UsageCard
            title="W.Sign uygulaması"
            sub="USB token ile imza"
            body="Nitelikli e-imza kartınızla imzalamak için Windows uygulamasını kurun; masaüstünde ve tarayıcıda çalışır."
            cta={{ label: 'İndir', to: '/download' }}
          />
          <UsageCard
            title="Kendi uygulamanıza gömün"
            sub="Yönlendirmeli imza veya API"
            body="Kullanıcıyı imza için yönlendirin ya da imzayı sunucudan yönetin. Entegrasyon dokümanı herkese açık."
            cta={{ label: 'Geliştirici dokümanı', to: '/docs' }}
          />
        </div>
      </section>

      {/* Closing CTA */}
      <section className="container-page py-12 pb-20">
        <div className="card bg-[var(--color-accent-wash)] border-[var(--color-accent)]/20 flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Kurumunuz için değerlendirin</h2>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              Ekibinizle bir tanıtım görüşmesi planlayalım, kurumunuza özel teklif çıkaralım.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/destek" className="btn-primary">İletişime geç</Link>
            <Link to="/verify" className="btn-ghost">İmza doğrula</Link>
          </div>
        </div>
      </section>
    </>
  )
}

function PillarCard({ icon: Icon, title, body }: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  title: string; body: string
}) {
  return (
    <div className="card">
      <Icon size={22} className="text-[var(--color-accent)]" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{body}</p>
    </div>
  )
}

function FlowStep({ n, icon: Icon, title, body }: {
  n: number
  icon: React.ComponentType<{ size?: number; className?: string }>
  title: string; body: string
}) {
  return (
    <div className="card flex flex-col">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--color-accent-wash)] text-sm font-semibold text-[var(--color-accent)]">
          {n}
        </span>
        <Icon size={20} className="text-[var(--color-accent)]" />
      </div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{body}</p>
    </div>
  )
}

interface FormatCardProps {
  title: string
  sub:   string
  items: string[]
  status: 'ready' | 'partial' | 'planned'
}

function FormatCard({ title, sub, items, status }: FormatCardProps) {
  const badge = status === 'ready'   ? <span className="badge badge-ok">hazır</span>
              : status === 'partial' ? <span className="badge badge-warn">kısmen</span>
              :                         <span className="badge">yol haritası</span>
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs font-mono text-[var(--color-fg-dim)]">{sub}</p>
        </div>
        {badge}
      </div>
      <ul className="mt-4 space-y-1.5 text-sm text-[var(--color-fg-muted)]">
        {items.map(item => <li key={item}>• {item}</li>)}
      </ul>
    </div>
  )
}

interface UsageCardProps {
  title: string
  sub:   string
  body:  string
  cta:   { label: string; to: string }
}

function UsageCard({ title, sub, body, cta }: UsageCardProps) {
  return (
    <div className="card flex flex-col">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs text-[var(--color-fg-dim)]">{sub}</p>
      <p className="mt-3 flex-1 text-sm text-[var(--color-fg-muted)]">{body}</p>
      <div className="mt-4">
        <Link to={cta.to} className="btn-ghost text-xs">{cta.label}</Link>
      </div>
    </div>
  )
}
